import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaUsers, FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";

import { fetchUsers, clearState, deleteUsers } from "../../redux/Slices/UsersSlice";
import { useTitle } from "../../context/TitleContext";
import PermissionMotionButton from "../PermissionMotionButton";
import PermissionButton from "../PermissionButton";
import Pagination from '../../components/Pagination';

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [query, setQuery] = useState("");

  const { users, meta, isLoading: loading, error, success } = useSelector(
    (state) => state.users
  );

  useEffect(() => {
    setTitle("المستخدمين");
  }, [setTitle]);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchUsers({ page: currentPage, search: query }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (success) {
      setShowDeleteModal(false);
      setDeletingUser(null);
      setTimeout(() => {
        toast.success("تم الحذف بنجاح", { position: "top-center", zIndex: 9999 });
      }, 100);
      
      const usersLength = Array.isArray(users) ? users.length : 0;
      if (usersLength === 0 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        dispatch(fetchUsers({ page: currentPage, search: query }));
      }
      dispatch(clearState());
    }
    if (error) {
      toast.error("لم يتم الحذف", { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, error, dispatch, currentPage, users?.length]);

  const handlePageChange = (page) => {
    if (loading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleDelete = (user) => {
    setDeletingUser(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingUser) {
      dispatch(deleteUsers(deletingUser.id));
      setShowDeleteModal(false);
      setDeletingUser(null);
    }
  };

  const handleSearchUsers = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    dispatch(fetchUsers({ page: 1, search: query }));
  };



  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  // BUG-40 fix: تصفية فورية على الفرونت بالاسم/الهاتف كضمان لعمل البحث
  const displayedUsers = Array.isArray(users)
    ? users.filter((u) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return (
          String(u.phone || "").toLowerCase().includes(q) ||
          String(u.name || "").toLowerCase().includes(q) ||
          String(u.parent_phone || "").toLowerCase().includes(q)
        );
      })
    : [];

  if (loading && (!users || users.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .user-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .user-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .user-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            👥 المستخدمين (الطلاب)
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل ومراقبة وإضافة حسابات الطلاب وتعيين الصفوف والمدن</p>
        </div>
        <PermissionMotionButton
          permission="create-users"
          onClick={() => navigate("/add-user")}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          <FaPlus size={12} /> إضافة مستخدم جديد
        </PermissionMotionButton>
      </div>

      {/* ── Search Bar ───────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "18px 22px", marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 8, textAlign: "right" }}>البحث في المستخدمين</label>
        <form className="d-flex align-items-center" onSubmit={handleSearchUsers} style={{ maxWidth: 500, gap: 10 }}>
          <input
            style={{
              width: "100%",
              padding: "9px 14px",
              borderRadius: 9,
              border: "1.5px solid #e2e8f0",
              fontSize: 14,
              outline: "none",
              boxSizing: "border-box",
              backgroundColor: "#fff",
              transition: "border-color 0.2s, box-shadow 0.2s"
            }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="بحث برقم الهاتف أو الاسم..."
            onFocus={(e) => {
              e.target.style.borderColor = "var(--main-color)";
              e.target.style.boxShadow = "0 0 0 3px rgba(228, 109, 23, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          />
          <button className="btn btn-primary btn-sm" type="submit" style={{ padding: "10px 18px", borderRadius: 9 }}><i className="bi bi-search"></i></button>
          {query && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setQuery("");
                setCurrentPage(1);
                dispatch(fetchUsers({ page: 1, search: "" }));
              }}
              style={{ padding: "9px 16px", borderRadius: 9, minWidth: "110px" }}
            >
              مسح البحث
            </button>
          )}
        </form>
      </div>

      {/* ── Table Container ────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", padding: 0 }}>
        {(!users || users.length === 0) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <FaUsers size={48} className="text-muted mb-3" />
            <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد مستخدمين حالياً</p>
          </div>
        ) : (
          <div style={{ padding: 0 }}>
            <div className="table-responsive">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الاسم</th>
                    <th>الصورة</th>
                    <th>الهاتف</th>
                    <th>هاتف ولي الأمر</th>
                    <th>الصف (عربي/EN)</th>
                    <th>القسم</th>
                    <th>المدينة</th>
                    <th>الحالة</th>
                    <th>تاريخ الإنشاء</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedUsers.map((user, index) => (
                    <tr key={user.id}>
                      <td>
                        {meta ? (meta.current_page - 1) * meta.per_page + index + 1 : index + 1}
                      </td>
                      <td style={{ fontWeight: 600, color: "#0f172a" }}>{user.name}</td>
                      <td>
                        <img
                          src={user?.image || "/user.webp"}
                          alt="Avatar"
                          onError={(e) => (e.target.src = "/user.webp")}
                          style={{ height: "40px", width: "40px", objectFit: "cover", borderRadius: "50%", border: "1.5px solid #e2e8f0" }}
                        />
                      </td>
                      <td style={{ color: "#475569" }}>{user.phone}</td>
                      <td style={{ color: "#475569" }}>{user.parent_phone || "--"}</td>
                      <td>
                        <div style={{ fontSize: 12, color: "#0f172a" }}>{user.grade?.name_ar || user.grade_name_ar || "--"}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>{user.grade?.name_en || user.grade_name_en || ""}</div>
                      </td>
                      <td>{user.section?.name_ar || user.section_name_ar || "--"}</td>
                      <td>{user.city?.name_ar || user.city_name_ar || "--"}</td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: (user.status === "active" || user.status === true) ? "#15803d" : "#64748b", background: (user.status === "active" || user.status === true) ? "#d1fae5" : "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
                          {(user.status === "active" || user.status === true) ? "نشط" : "غير نشط"}
                        </span>
                      </td>
                      <td>{new Date(user.created_at).toLocaleDateString('ar-EG')}</td>
                      <td>
                        <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                          <PermissionButton
                            onClick={() => {
                              sessionStorage.setItem(`user_${user.id}`, JSON.stringify({ id: user.id, name: user.name }));
                              navigate(`/edit-user/${user.id}`);
                            }}
                            permission="edit-users"
                            title="تعديل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEdit size={14} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => navigate(`/user-details/${user.id}`)}
                            permission="view-users"
                            title="التفاصيل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #bae6fd", background: "#e0f2fe", color: "#0284c7", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEye size={12} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => handleDelete(user)}
                            permission="delete-users"
                            title="حذف"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaTrash size={14} />
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0 16px 20px" }}>
              <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingUser(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف حساب الطالب التالي؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                👤 {deletingUser?.name}
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه وسيحذف كافة عهد الطالب وسجلاته.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingUser(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer" }}>حذف المستخدم</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
